/**
 * Admin — Revenue Calculation, the assumptions and provenance panel (card 3065).
 *
 * The nine cost lines X authorized on 2026-08-28, served by
 * `GET /admin/revenue-calculation/assumptions`.
 *
 * 🔑 NOTHING HERE IS AUTHORED IN THE VIEW. Every amount, date, authorizer and provenance
 * sentence arrives from the API, which reads the frozen `costAssumptions` registry. A ruled
 * figure cannot be quietly changed, softened or re-dated by editing a component.
 *
 * 🔑 EVERY LINE SAYS IT IS AN ASSUMPTION. Each row renders
 * "ASSUMPTION — effective 2026-08-28, authorized by X". These are dated decisions, not vendor
 * invoices and not verified rates, and a reader who cannot tell the difference will treat a
 * ruling as a receipt. The panel additionally REFUSES to render a payload containing invoice
 * language (`gradeAssumptionsPayload`) rather than displaying it with a caveat.
 *
 * 🔑 A RULED ZERO AND AN UNKNOWN ARE RENDERED DIFFERENTLY, ON PURPOSE, ON THE SAME PAGE.
 * A ruled zero reads "$0.00 (ruled zero — assumption)"; an unknown reads `— / Missing`, the
 * literal owned by `utils/revenueMetrics.js` and rendered by the product table above. The
 * contrast legend puts both tokens side by side so the distinction is legible in one look.
 *
 * ⛔ The render rules themselves live in `utils/revenueAssumptions.js`, NOT here: jest cannot
 * import a `.jsx` file (`moduleFileExtensions` has no `jsx`), so a rule written inline would
 * be a rule no test can pin.
 */

import { useState } from 'react';
import {
  MISSING_TEXT,
  RULED_ZERO_SUFFIX,
  formatAssumptionAmount,
  describeAssumptionLabel,
  gradeAssumptionsPayload,
  partitionAssumptions
} from '../../utils/revenueAssumptions';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/** Pure. Side-by-side proof that a decided zero and an unknown are not the same fact. */
export const AssumptionContrast = () => (
  <p className={styles.assumptionContrast} data-testid="assumption-contrast">
    <span className={styles.ruledZeroToken} data-testid="contrast-ruled-zero">
      $0.00 {RULED_ZERO_SUFFIX}
    </span>
    <span className={styles.assumptionContrastSep}>is a decision X made.</span>
    <span className={styles.missingToken} data-testid="contrast-missing">{MISSING_TEXT}</span>
    <span className={styles.assumptionContrastSep}>
      is a gap nobody has filled. They are never rendered the same way, and an unknown is
      never shown as a dollar amount.
    </span>
  </p>
);

/** Pure. One ruled line. `detailOpen` reveals provenance without hiding the label. */
export const AssumptionRow = ({ line, detailOpen }) => (
  <li className={styles.blockerCard} data-testid={`assumption-${line.key}`} data-ruled-zero={String(Boolean(line.isRuledZero))}>
    <div className={styles.assumptionHead}>
      <span className={styles.assumptionName}>{line.label}</span>
      <span
        className={line.isRuledZero ? styles.ruledZeroToken : styles.assumptionAmount}
        data-testid={`assumption-amount-${line.key}`}
      >
        {formatAssumptionAmount(line)}
      </span>
    </div>

    <p className={styles.assumptionLabel} data-testid={`assumption-label-${line.key}`}>
      {describeAssumptionLabel(line)}
    </p>

    {detailOpen && (
      <dl className={styles.blockerBody}>
        <dt className={styles.blockerTerm}>Provenance</dt>
        <dd className={styles.blockerDef}>{line.provenance}</dd>

        <dt className={styles.blockerTerm}>Kind</dt>
        <dd className={styles.blockerDef}>
          {line.kind}
          {line.isRuledZero ? ` · ${line.reasonCode}` : ''}
        </dd>

        <dt className={styles.blockerTerm}>Effective</dt>
        <dd className={styles.blockerDef}>
          from {line.effectiveFrom}
          {line.effectiveTo ? ` to ${line.effectiveTo}` : ' · no end date set'}
        </dd>

        {line.supersedes && (
          <>
            <dt className={styles.blockerTerm}>Supersedes</dt>
            <dd className={styles.blockerDef} data-testid={`assumption-supersedes-${line.key}`}>
              {typeof line.supersedes.priorAmountCents === 'number'
                ? `Prior amount ${line.supersedes.priorAmountCents} cents`
                : 'A prior value'}
              {line.supersedes.priorSymbol ? ` (${line.supersedes.priorSymbol}` : ''}
              {line.supersedes.priorLocation ? ` at ${line.supersedes.priorLocation})` : line.supersedes.priorSymbol ? ')' : ''}
              {line.supersedes.note ? ` — ${line.supersedes.note}` : ''}
            </dd>
          </>
        )}
      </dl>
    )}
  </li>
);

/** Pure. A question this board ANSWERED — kept visible so the answer has a trail. */
export const AnsweredQuestion = ({ answer }) => (
  <li className={styles.blockerCard} data-testid={`answered-${answer.id}`}>
    <div className={styles.assumptionHead}>
      <span className={styles.blockerCode}>{answer.id}</span>
      <span className={styles.assumptionName}>{answer.question}</span>
    </div>
    <dl className={styles.blockerBody}>
      <dt className={styles.blockerTerm}>Answer</dt>
      <dd className={styles.blockerDef}>{answer.answer}</dd>
      <dt className={styles.blockerTerm}>Authority</dt>
      <dd className={styles.blockerDef}>
        {answer.answeredBy} · {answer.kind} · implemented in <code>{answer.implementedIn}</code>
      </dd>
    </dl>
  </li>
);

/**
 * Pure. The panel.
 *
 * ⛔ A faulted payload is REFUSED, not decorated. Rendering nine plausible money lines from a
 * payload that failed its own provenance guard would publish exactly the misrepresentation
 * this panel exists to prevent, and the reader would have no way to know.
 */
const RevenueAssumptionsPanel = ({ assumptions }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const grade = gradeAssumptionsPayload(assumptions);

  if (!grade.ok) {
    return (
      <div className={styles.assumptionFault} data-testid="assumptions-fault">
        <p className={styles.assumptionFaultTitle}>
          These assumptions are not being rendered, because the panel cannot vouch for them.
        </p>
        <ul className={styles.blocksList} data-testid="assumptions-fault-reasons">
          {grade.reasons.map((reason) => (
            <li key={reason} className={styles.blocksItem}>{reason}</li>
          ))}
        </ul>
      </div>
    );
  }

  const { ruledZero, priced } = partitionAssumptions(assumptions.assumptions);

  return (
    <div className={styles.assumptions} data-testid="assumptions-panel">
      <p className={styles.blockersLead} data-testid="assumptions-lead">
        <strong>{assumptions.count}</strong> cost lines, every one an{' '}
        <strong>assumption with an effective date</strong> — authorized by{' '}
        {assumptions.authorizedBy} on {assumptions.ratifiedOn}, recorded at assumptions
        version {assumptions.assumptionsVersion} and applied by economics version{' '}
        <strong data-testid="assumptions-economics-version">{assumptions.economicsVersion}</strong>.
        Not one of these is a vendor invoice, a vendor quote or a verified rate.
      </p>

      <AssumptionContrast />

      <button
        type="button"
        className={styles.assumptionToggle}
        onClick={() => setDetailOpen((open) => !open)}
        aria-expanded={detailOpen}
        data-testid="assumptions-toggle"
      >
        {detailOpen ? 'Hide provenance detail' : 'Show provenance detail'}
      </button>

      <p className={styles.assumptionGroupTitle} data-testid="assumptions-priced-title">
        Priced lines
      </p>
      <ul className={styles.blockerList} data-testid="assumptions-priced">
        {priced.map((line) => <AssumptionRow key={line.key} line={line} detailOpen={detailOpen} />)}
      </ul>

      <p className={styles.assumptionGroupTitle} data-testid="assumptions-ruled-zero-title">
        Ruled-zero lines · {assumptions.ruledZeroCount} of {assumptions.count}
      </p>
      <p className={styles.sectionNote} data-testid="assumptions-ruled-zero-note">
        X ruled each of these to be zero. That is a decision that the amount IS zero — it is not
        a missing number, and it is not a discount. An unknown cost is <code>null</code>, never{' '}
        <code>0</code>, because zero is a real price meaning free.{' '}
        <strong>
          The 2026-08-28 ruling table lists three ruled-zero lines while this registry holds{' '}
          {assumptions.ruledZeroCount}
        </strong>{' '}
        — the table records the DrCare and Doctor Now monthly provider fees as a single row, and
        the registry keeps one entry per provider. Both counts are correct; they count different
        things.
      </p>
      <ul className={styles.blockerList} data-testid="assumptions-ruled-zero">
        {ruledZero.map((line) => <AssumptionRow key={line.key} line={line} detailOpen={detailOpen} />)}
      </ul>

      {(assumptions.answeredQuestions ?? []).length > 0 && (
        <>
          <p className={styles.assumptionGroupTitle} data-testid="assumptions-answered-title">
            Answered on 2026-08-28 · no longer blockers
          </p>
          <p className={styles.sectionNote}>
            These were open money questions on this page. They are recorded here, rather than
            deleted, so that a figure sourced from a ruling can be told apart from one that was
            never in question.
          </p>
          <ul className={styles.blockerList} data-testid="assumptions-answered">
            {assumptions.answeredQuestions.map((answer) => (
              <AnsweredQuestion key={answer.id} answer={answer} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default RevenueAssumptionsPanel;
